import { isDebugEnabled } from "./debugLog";

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

const LOG_COLORS = {
  ERROR: "#ff4444",
  WARN: "#ffaa00",
  INFO: "#00aa00",
  DEBUG: "#0066cc",
};

class FrontendLogger {
  constructor() {
    // Errors and warnings always print. Info/debug traces only print in a
    // development build with the debug flag on (see utils/debugLog) — they
    // produced thousands of console messages during a live match (BUG-43).
    this.level = LOG_LEVELS.WARN;
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
  }

  generateSessionId() {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  shouldLog(level) {
    const effective = isDebugEnabled() ? LOG_LEVELS.DEBUG : this.level;
    return level <= effective;
  }

  formatMessage(level, message, data = {}) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS)[level];

    return {
      timestamp,
      level: levelName,
      message,
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...data,
    };
  }

  log(level, message, data = {}) {
    if (!this.shouldLog(level)) return;

    const levelName = Object.keys(LOG_LEVELS)[level];
    const formattedData = this.formatMessage(level, message, data);
    const color = LOG_COLORS[levelName];

    // Console output with styling
    console.group(
      `%c[${levelName}] ${message}`,
      `color: ${color}; font-weight: bold`
    );
    console.log("Time:", formattedData.timestamp);
    console.log("Session:", formattedData.sessionId);
    if (Object.keys(data).length > 0) {
      console.log("Data:", data);
    }
    console.groupEnd();

    if (process.env.NODE_ENV === "production" && level <= LOG_LEVELS.WARN) {
      this.sendToLoggingService(formattedData);
    }
  }

  error(message, data = {}) {
    this.log(LOG_LEVELS.ERROR, message, data);

    if (data.error && data.error.stack) {
      console.error("Stack trace:", data.error.stack);
    }
  }

  warn(message, data = {}) {
    this.log(LOG_LEVELS.WARN, message, data);
  }

  info(message, data = {}) {
    this.log(LOG_LEVELS.INFO, message, data);
  }

  debug(message, data = {}) {
    this.log(LOG_LEVELS.DEBUG, message, data);
  }

  logApiCall(method, url, status, duration, data = {}) {
    const logData = {
      method,
      url,
      status,
      duration: `${duration}ms`,
      ...data,
    };

    if (status >= 400) {
      this.error(`API Error: ${method} ${url}`, logData);
    } else {
      this.debug(`API Call: ${method} ${url}`, logData);
    }
  }

  logUserAction(action, details = {}) {
    this.info(`User Action: ${action}`, {
      action,
      timestamp: Date.now(),
      ...details,
    });
  }

  logNavigation(from, to) {
    this.info("Navigation", {
      from,
      to,
      timestamp: Date.now(),
    });
  }

  logError(error, context = {}) {
    this.error("Application Error", {
      message: error.message,
      name: error.name,
      stack: error.stack,
      context,
      error,
    });
  }

  logPerformance(metric, value, unit = "ms") {
    this.debug("Performance Metric", {
      metric,
      value,
      unit,
      timestamp: Date.now(),
    });
  }

  // Method to send logs to external service (implement as needed)
  sendToLoggingService(logData) {
    // Implement integration with logging service like LogRocket, Sentry, etc.
    // For now, we'll just store in localStorage for development
    try {
      const logs = JSON.parse(localStorage.getItem("app_logs") || "[]");
      logs.push(logData);

      // Keep only last 100 logs in localStorage
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }

      localStorage.setItem("app_logs", JSON.stringify(logs));
    } catch (e) {
      console.error("Failed to store log:", e);
    }
  }

  getLogs() {
    try {
      return JSON.parse(localStorage.getItem("app_logs") || "[]");
    } catch (e) {
      console.error("Failed to retrieve logs:", e);
      return [];
    }
  }

  clearLogs() {
    try {
      localStorage.removeItem("app_logs");
      this.info("Logs cleared");
    } catch (e) {
      console.error("Failed to clear logs:", e);
    }
  }
}

const logger = new FrontendLogger();

export { FrontendLogger, logger };
export default logger;
